/**
 * Composition boundary for selecting a platform client implementation.
 *
 * UI shells depend on this runtime rather than constructing an adapter
 * directly. Microsoft Entra is the sole identity provider, so the only runtime
 * talks to the platform API over GraphQL and resolves the signed-in user from
 * the bearer token.
 *
 * @module platform/runtime
 */

import type { ProgrammePlatformClient } from "./client";
import {
  GraphqlProgrammePlatformClient,
  type GraphqlProgrammePlatformClientOptions,
} from "./graphql-client";

export interface ProgrammePlatformClientContext {
  activeUserId?: string;
  activeTenantId?: string;
}

export interface ProgrammePlatformRuntime {
  createClient(context?: ProgrammePlatformClientContext): ProgrammePlatformClient;
}

export function createGraphqlProgrammePlatformRuntime(
  options: GraphqlProgrammePlatformClientOptions,
): ProgrammePlatformRuntime {
  // One client per active tenant. A user with several memberships switches
  // tenant by asking for a client in a different context, and that client must
  // carry the new tenant on its requests — a single shared instance would keep
  // acting in whichever tenant it was first built for. Token acquisition stays
  // per-request via `getAccessToken`, so caching the client is only caching
  // transport configuration, not identity.
  const clientsByTenant = new Map<string, GraphqlProgrammePlatformClient>();
  return {
    createClient: (context = {}) => {
      const key = context.activeTenantId ?? "";
      let client = clientsByTenant.get(key);
      if (!client) {
        client = new GraphqlProgrammePlatformClient({
          ...options,
          tenantId: context.activeTenantId,
        });
        clientsByTenant.set(key, client);
      }
      return client;
    },
  };
}
